import React, { useEffect, useState } from "react";
import styles from "./Header.module.scss"
import {SVGAdiona, SVGSun, SVGMoon} from "../assets/PackageSVG";
import { useAuth0 } from "@auth0/auth0-react";
import {LogInButton} from "./LogInButton";
import {LogOutButton} from "./LogOutButton";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

type HeaderProps = {
    compact?: boolean;
};

const NAV_LINKS = [
    { to: '/about', label: 'About' },
    { to: '/business', label: 'For Business' },
    { to: '/chat', label: 'Chat' },
];

export const Header = ({ compact = false }: HeaderProps)=> {
    const { user, isLoading, isAuthenticated } = useAuth0();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    /* The links collapse into this menu below 860px, where the row no
       longer fits beside the wordmark and the account controls. */
    const [menuOpen, setMenuOpen] = useState(false);
    useEffect(() => setMenuOpen(false), [location.pathname]);
    useEffect(() => {
        if (!menuOpen) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [menuOpen]);

    /* The header floats transparently over the hero photo, which leaves it
       unreadable the moment the page scrolls and cards pass underneath it.
       Past the first screenful it takes on a background of its own. */
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <nav className={`${styles.Header} ${compact ? styles.HeaderCompact : ''} ${scrolled ? styles.HeaderScrolled : ''}`}>
            <div className={`${styles.navDiv} ${compact ? styles.navDivCompact : ''}`}>
                {/* The links used to sit inside this Link — nested anchors,
                    which the browser unnests, so they navigated to "/". */}
                <div className={`${styles.navLeft} ${compact ? styles.navLeftCompact : ''}`}>
                    <Link to={"/"} className={`${styles.logoContainer} ${compact ? styles.logoContainerCompact : ''}`}>
                        <SVGAdiona/>
                        {/*<div className={styles.logoBox}>*/}
                        {/*    <span className={styles.logoLetter}>M</span>*/}
                        {/*</div>*/}
                        <span className={`${styles.logoText} ${compact ? styles.logoTextCompact : ''}`}>Adiona Travel</span>
                    </Link>

                    <div className={`${styles.linksRow} ${compact ? styles.linksRowCompact : ''}`}>
                        {NAV_LINKS.map(({ to, label }) => (
                            <Link
                                key={to}
                                to={to}
                                className={`${styles.navLink} ${compact ? styles.navLinkCompact : ''}`}
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>

                <div className={`${styles.logInContainer} ${compact ? styles.logInContainerCompact : ''}`}>
                    <button
                        className={`${styles.themeToggle} ${compact ? styles.themeToggleCompact : ''}`}
                        onClick={toggleTheme}
                        type="button"
                        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                    >
                        {theme === 'dark' ? <SVGSun width="18" height="21" stroke="#d1d5db" /> : <SVGMoon width="18" height="18" stroke="#d1d5db" />}
                    </button>
                    {isAuthenticated ? (
                        <button className={`${styles.logInButton} ${compact ? styles.logInButtonCompact : ''}`}>
                            <LogOutButton />
                        </button>
                    ) : (
                        <button className={`${styles.logInButton} ${compact ? styles.logInButtonCompact : ''}`}>
                            <LogInButton />
                        </button>
                    )}
                    <button
                        className={styles.menuBtn}
                        onClick={() => setMenuOpen(prev => !prev)}
                        type="button"
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}
                    >
                        {menuOpen
                            ? <X size={18} aria-hidden="true" />
                            : <Menu size={18} aria-hidden="true" />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div className={styles.mobileMenu}>
                    {NAV_LINKS.map(({ to, label }) => (
                        <Link
                            key={to}
                            to={to}
                            className={styles.mobileMenuLink}
                            onClick={() => setMenuOpen(false)}
                        >
                            {label}
                        </Link>
                    ))}
                </div>
            )}
        </nav>
    )
}